// bundles/livcg/panels/src/match-day/deck/registry.tsx
// Deck widget registry: metadata + Render for each tile type.
// DeckView (Task 8) draws the catalog and the current tiles from this list.
import type { ReactElement } from "react";
import type { ProjectState } from "@livcg/schema";
import { OVERLAY_GROUPS, overlayModeLabel } from "../overlayModes.js";
import type { OverlayExtras } from "../control-room/overlayData.js";
import type { useOverlaySwitch } from "../control-room/useOverlaySwitch.js";
import type { useQuickEventChain } from "../control-room/useQuickEventChain.js";
import {
  AllOffWidget,
  AutoSponsorWidget,
  ClockWidget,
  EventsWidget,
  ExtraTimeWidget,
  HideWidget,
  OverlayTileWidget,
  ScoreWidget,
  VarWidget,
} from "./widgets.js";

export type DeckServices = {
  overlay: ReturnType<typeof useOverlaySwitch>;
  extras: OverlayExtras;
  allOff: () => void;
  /** A single useQuickEventChain instance at the DeckView level — if each EventsWidget
   *  called its own, pressing SARIQ from away during a home GOL chain would make
   *  two independent sets of timers collide (same as the overlay pattern). */
  chain: ReturnType<typeof useQuickEventChain>;
};

export type DeckWidgetProps = {
  slug: string;
  state: ProjectState;
  w: number;
  h: number;
  /** The tile's `widget` field (e.g. "overlay:big", "events:home") —
   *  DeckGrid (Task 8) always passes it. Only OverlayTileWidget/EventsWidget
   *  read it (they derive mode/side from it). */
  widgetId: string;
  services: DeckServices;
};

export type DeckWidgetDef = {
  widget: string;
  label: string;
  /** For grouping in the catalog ("Boshqaruv" or an OVERLAY_GROUPS name). */
  group: string;
  minW: number;
  minH: number;
  Render: (p: DeckWidgetProps) => ReactElement;
};

const CORE: DeckWidgetDef[] = [
  { widget: "clock", label: "Soat", group: "Boshqaruv", minW: 2, minH: 1, Render: ClockWidget },
  { widget: "score", label: "Hisob", group: "Boshqaruv", minW: 2, minH: 1, Render: ScoreWidget },
  { widget: "events:home", label: "Hodisalar · H", group: "Boshqaruv", minW: 3, minH: 2, Render: EventsWidget },
  { widget: "events:away", label: "Hodisalar · A", group: "Boshqaruv", minW: 3, minH: 2, Render: EventsWidget },
  { widget: "var", label: "VAR", group: "Boshqaruv", minW: 2, minH: 2, Render: VarWidget },
  { widget: "all-off", label: "ALL OFF", group: "Boshqaruv", minW: 1, minH: 1, Render: AllOffWidget },
  { widget: "hide", label: "Yashirish", group: "Boshqaruv", minW: 1, minH: 1, Render: HideWidget },
  { widget: "extra-time", label: "Extra vaqt", group: "Boshqaruv", minW: 1, minH: 1, Render: ExtraTimeWidget },
  { widget: "auto-sponsor", label: "Auto sponsor", group: "Boshqaruv", minW: 1, minH: 1, Render: AutoSponsorWidget },
];

/** Each overlay mode is a separate 1×1 tile (added without duplicates). */
const OVERLAYS: DeckWidgetDef[] = OVERLAY_GROUPS.flatMap((g) =>
  g.modes.map((m) => ({
    widget: `overlay:${m.id}`,
    label: overlayModeLabel(m.id),
    group: g.name,
    minW: 1,
    minH: 1,
    Render: OverlayTileWidget,
  })),
);

export const WIDGET_DEFS: DeckWidgetDef[] = [...CORE, ...OVERLAYS];

export function widgetDef(id: string): DeckWidgetDef | null {
  return WIDGET_DEFS.find((d) => d.widget === id) ?? null;
}
